import { HomeSettingIcon, PencilPaperIcon, TaskSearchIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import { type Tile } from "@vega-modules/scenes/CoInvestmentsLP/components/Welcome/WelcomeStepsTiles";

type Props = {
  onSubmit: VoidFunction;
  isSubmitting?: boolean;
  heading?: string;
  description?: string;
  tiles?: Tile[];
};

export const WelcomeStepView = ({
  onSubmit,
  isSubmitting = false,
  heading,
  description,
  tiles: tilesProp,
}: Props) => {
  const {
    global: { companyName },
  } = usePublicConfig();

  const defaultTiles: Tile[] = [
    {
      icon: <TaskSearchIcon />,
      title: m.welcome_landing_browseTitle(),
      description: m.welcome_landing_browseDescription({ companyName }),
    },
    {
      icon: <PencilPaperIcon />,
      title: m.welcome_landing_subscribeTitle(),
      description: m.welcome_landing_subscribeDescription(),
    },
    {
      icon: <HomeSettingIcon />,
      title: m.welcome_landing_operationsTitle(),
      description: m.welcome_landing_operationsDescription(),
    },
  ];

  const tiles = tilesProp ?? defaultTiles;

  return (
    <div className="max-w-4xl p-6 md:p-8 flex flex-col items-center gap-8">
      <div className="w-full flex flex-col items-center gap-1">
        <h4 className="heading-4 px-16 ">
          {heading ?? m.welcome_landing_heading({ companyName })}
        </h4>
        {description !== undefined ? (
          <p
            className="text-text/secondary body-small-regular text-center"
            data-testid="welcome-description"
          >
            {description}
          </p>
        ) : (
          <ThemedButton
            className="text-text/secondary  body-small-regular no-underline"
            dataTestId="subheading-button"
            onClick={() => {
              window.open(
                "https://www.vega-alts.com/",
                "_blank",
                "noopener,noreferrer",
              );
            }}
            variant="text"
          >
            {m.welcome_landing_subheading()}
          </ThemedButton>
        )}
      </div>

      <div className="w-full bg-background/primary rounded-lg border border-border/primary flex flex-col divide-border/border-primary lg:flex-row divide-y lg:divide-x lg:divide-y-0">
        {tiles.map((tile) => (
          <div className="flex-1 p-6 flex flex-col gap-6" key={tile.title}>
            {tile.icon ? (
              <div className="w-6 h-6 flex justify-center items-center text-text/secondary">
                {tile.icon}
              </div>
            ) : null}
            <div className="flex-1 flex flex-col gap-2">
              <div className="body-semi-large-semibold text-text/primary text-left">
                {tile.title}
              </div>
              <div className="body-base-regular text-text/secondary text-left">
                {tile.description}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="w-full flex items-center justify-center">
        <ThemedButton
          className="w-48"
          dataTestId="continue-button"
          fullWidth
          isLoading={isSubmitting}
          onClick={onSubmit}
          variant="primary"
        >
          {m.btn_continue()}
        </ThemedButton>
      </div>
    </div>
  );
};
